/**
 * Which conversations hold unsent text — the reactive half of `use-channel-draft`.
 *
 * The draft itself is deliberately NOT reactive: the composer keeps it in a ref and
 * mirrors it to `localStorage` on a debounce, because nothing needs to re-render while
 * someone types into the box they are already looking at. The inbox does — a row has to
 * grow its draft mark the moment a draft exists and lose it the moment it is sent, from
 * a component that shares no ancestor with the composer and never mounts one.
 *
 * So this module holds the one bit the rest of the slice wants — "is there a draft for
 * this cid?" — as a store, in the shape `use-pending-sends.ts` already uses next door:
 * a private zustand store whose whole surface is the module-level writers below, plus a
 * selector hook that hands a row its own answer and nothing else.
 *
 * ## Two speeds
 *
 * The index is updated SYNCHRONOUSLY by the composer's mutators, ahead of their debounced
 * write, so the mark tracks the keystroke rather than the save. The index may therefore
 * run up to a second ahead of `localStorage`; it is never behind, and a reload re-derives
 * it from storage, so the two cannot drift across a session.
 *
 * ## Keys in, cids out
 *
 * Callers speak in draft KEYS, because that is what a composer holds; a row asks by CID.
 * `_libs/channel-draft-keys` owns the translation, and it is the narrow point of the
 * whole feature: an internal note and a thread reply persist their drafts like anything
 * else but translate to no row, so they never enter this store at all. What does enter
 * is one bare cid per key, which is why the state is a single Set rather than a key set
 * with a derived one beside it.
 *
 * ## Same tab vs other tabs
 *
 * The `storage` event does NOT fire in the tab that wrote the value, so it covers only
 * other tabs; same-tab updates come from `setChannelDraftPresence`. Neither path
 * substitutes for the other.
 */

import { create } from "zustand";

import {
  cidOfChannelDraftStorageKey,
  collectCidsWithDraft,
  isChannelDraftStorageKey,
} from "../_libs/channel-draft-keys";

/**
 * Storage may be unreadable — a private window, a disabled-storage policy, or the `node`
 * test environment where the global does not exist at all. An empty answer is the right
 * one in every case: nothing can be read, and nothing will be written either.
 */
function readStoredDraftCids(): ReadonlySet<string> {
  try {
    return collectCidsWithDraft(Object.keys(localStorage));
  } catch {
    return new Set();
  }
}

type ChannelDraftIndexStore = {
  /** The conversations holding a draft — an O(1) lookup per row. */
  cids: ReadonlySet<string>;
  setPresence: (storageKey: string, present: boolean) => void;
  resync: () => void;
};

/**
 * Private — the module-level writers below are the whole surface, so nothing can reach
 * past them into `setState`.
 *
 * Hydrated eagerly from storage: the mark has to be right on the FIRST render of a list
 * that was drawn before any composer mounted, which is exactly the reload case.
 */
const useChannelDraftIndexStore = create<ChannelDraftIndexStore>()(
  (set, get) => ({
    cids: readStoredDraftCids(),

    setPresence: (storageKey, present) => {
      // An internal note and a thread reply mark no row. They still persist like any
      // other draft — they just never reach the index, so typing a note does not notify
      // every conversation row in the list.
      const cid = cidOfChannelDraftStorageKey(storageKey);
      if (!cid) return;

      const { cids } = get();
      // A 200-character message is one presence change, not 200 — this early return is
      // what keeps a burst of typing to a single notification.
      if (cids.has(cid) === present) return;

      const next = new Set(cids);
      if (present) next.add(cid);
      else next.delete(cid);
      set({ cids: next });
    },

    resync: () => set({ cids: readStoredDraftCids() }),
  }),
);

/**
 * Record whether one draft key holds anything worth keeping.
 *
 * Called by `useChannelDraft` on every composer mutation — see "Two speeds".
 */
export function setChannelDraftPresence(
  storageKey: string,
  present: boolean,
): void {
  useChannelDraftIndexStore.getState().setPresence(storageKey, present);
}

/** Storage changed underneath us — another tab wrote, or every draft was wiped. */
export function resyncChannelDraftIndex(): void {
  useChannelDraftIndexStore.getState().resync();
}

// One listener for the whole app, attached at import. The guard is not for SSR — the
// webapp is a client-only SPA — but so the module stays import-safe under `node` vitest.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    // A `null` key is another tab's `localStorage.clear()` and must resync. Any other
    // key names a single entry, and the app writes plenty that are not ours.
    if (event.key !== null && !isChannelDraftStorageKey(event.key)) return;
    resyncChannelDraftIndex();
  });
}

/**
 * Whether this conversation holds unsent text — its own composer's, or a reply
 * half-written in one of its threads.
 *
 * A boolean, never the set: zustand's `useStore` is `useSyncExternalStore` with no
 * equality function, so a selector returning a fresh object would re-render forever.
 */
export function useHasChannelDraft(cid: string): boolean {
  return useChannelDraftIndexStore((state) => {
    return cid ? state.cids.has(cid) : false;
  });
}
